import { useEffect, useRef, useState } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";

export default function useAddressAutocomplete(addressQuery, setAddress) {
  const placesLib = useMapsLibrary("places");

  const [suggestions, setSuggestions] = useState([]);

  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const sessionTokenRef = useRef(null);

  useEffect(() => {
    if (!placesLib) return;

    autocompleteServiceRef.current = new placesLib.AutocompleteService();
    sessionTokenRef.current = new placesLib.AutocompleteSessionToken();
    const mapDiv = document.createElement("div");
    const map = new window.google.maps.Map(mapDiv);
    placesServiceRef.current = new placesLib.PlacesService(map);
  }, [placesLib]);

  useEffect(() => {
    if (!autocompleteServiceRef.current || !addressQuery || addressQuery?.length < 3) {
      setSuggestions([]);
      return;
    }

    autocompleteServiceRef.current.getPlacePredictions(
      {
        input: addressQuery,
        sessionToken: sessionTokenRef.current,
        types: ["address"],
        componentRestrictions: { country: "us" },
      },
      (predictions, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) {
          setSuggestions([]);
          return;
        }
        setSuggestions(predictions);
      }
    );
  }, [addressQuery]);

  const selectPlace = (placeId) => {
    placesServiceRef.current.getDetails(
      {
        placeId,
        fields: ["address_components"],
        sessionToken: sessionTokenRef.current,
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          const parsed = parseAddress(place);
          setAddress(parsed);
          setSuggestions([]);
        }
      }
    );
  };

  const parseAddress = (place) => {
    const mapping = {
      street_number: "",
      route: "",
      locality: "city",
      administrative_area_level_1: "state",
      postal_code: "zipCode",
    };

    const result = { address: "", city: "", state: "", zipCode: "" };

    for (const comp of place.address_components) {
      const type = comp.types[0];
      if (mapping[type]) {
        result[mapping[type]] = comp.short_name;
      }
    }

    const num = place.address_components.find((c) => c.types.includes("street_number"))?.long_name;
    const route = place.address_components.find((c) => c.types.includes("route"))?.long_name;

    result.address = `${num || ""} ${route || ""}`.trim();
    return result;
  };

  return {
    suggestions,
    selectPlace,
  };
}
